import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Trash2, Copy, Check, X, Building2, Globe, Lock, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getOrg, getOrgMembers, updateOrgMember, removeOrgMember, searchOrgUsers,
  createOrgInvite, getOrgInvites, revokeOrgInvite, updateOrg,
  getJoinRequests, approveJoinRequest, rejectJoinRequest, transferOrgOwnership,
} from '../api';
import { getActiveOrg } from '../org';

// Ownership is handed over explicitly, so it is never one of the options here.
const ROLES = ['member', 'admin'];

export default function Members() {
  const orgId = getActiveOrg();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [memberSearchInput, setMemberSearchInput] = useState('');   // bound to the input, updates every keystroke
  const [memberSearch, setMemberSearch] = useState('');             // debounced value actually queried
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [results, setResults] = useState([]);
  const [copied, setCopied] = useState(null);

  const me = localStorage.getItem('admin_email');
  const allows = (perm) => (org?.permissions || []).includes(perm);
  const isAdmin = allows('members.invite');
  const flash = (text, type = 'success') => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // Org info + invites/join-requests — independent of the member page/search,
  // so paging or searching the roster below never refetches these too.
  const loadOrgData = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const o = await getOrg(orgId);
      setOrg(o.data);
      if ((o.data.permissions || []).includes('members.invite')) {
        const [inv, req] = await Promise.all([getOrgInvites(orgId), getJoinRequests(orgId)]);
        setInvites(inv.data.invites || []); setRequests(req.data.requests || []);
      }
    } catch (e) { flash(e.response?.data?.detail || 'Failed to load', 'error'); }
  }, [orgId]);

  const loadMembers = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const m = await getOrgMembers(orgId, page, 50, memberSearch.trim());
      setMembers(m.data.members || []);
      setPages(m.data.pages || 1);
    } catch (e) { flash(e.response?.data?.detail || 'Failed to load members', 'error'); }
    finally { setLoading(false); }
  }, [orgId, page, memberSearch]);

  // A role change, removal, or transfer only needs the member page refreshed;
  // an invite/approval also touches org-level invite/request state.
  const load = useCallback(() => Promise.all([loadOrgData(), loadMembers()]), [loadOrgData, loadMembers]);

  useEffect(() => { loadOrgData(); }, [loadOrgData]);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  // Debounced so typing doesn't fire a request per keystroke — same pattern
  // as the invite-search box below. Resets to page 1 whenever the query
  // actually changes (not on every keystroke, only once it settles).
  useEffect(() => {
    const t = setTimeout(() => { setMemberSearch(memberSearchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [memberSearchInput]);

  useEffect(() => {
    if (!isAdmin || (email || '').trim().length < 2) { setResults([]); return; }
    let alive = true;
    const t = setTimeout(() => { searchOrgUsers(orgId, email.trim()).then((r) => alive && setResults(r.data.users || [])).catch(() => {}); }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [email, isAdmin, orgId]);

  const invite = async () => {
    if (!email.trim()) return;
    try {
      const r = await createOrgInvite(orgId, email.trim(), role);
      navigator.clipboard?.writeText(window.location.origin + r.data.accept_path).catch(() => {});
      flash(`Invite created for ${email.trim()} — link copied`); setEmail(''); setResults([]); await load();
    } catch (e) { flash(e.response?.data?.detail || 'Invite failed', 'error'); }
  };
  const decide = async (rid, approve) => {
    try { await (approve ? approveJoinRequest : rejectJoinRequest)(orgId, rid); flash(approve ? 'Approved' : 'Declined'); await load(); }
    catch (e) { flash(e.response?.data?.detail || 'Failed', 'error'); }
  };
  const changeRole = async (uid, r) => { try { await updateOrgMember(orgId, uid, r); flash('Role updated'); await load(); } catch (e) { flash(e.response?.data?.detail || 'Failed', 'error'); } };
  const remove = async (uid) => { if (!window.confirm('Remove this member?')) return; try { await removeOrgMember(orgId, uid); flash('Removed'); await load(); } catch (e) { flash(e.response?.data?.detail || 'Failed', 'error'); } };
  const transfer = async (uid, mail) => {
    if (!window.confirm(`Make ${mail} the owner? You'll become an admin.`)) return;
    try { await transferOrgOwnership(orgId, uid); flash(`${mail} is now the owner`); await load(); }
    catch (e) { flash(e.response?.data?.detail || 'Transfer failed', 'error'); }
  };
  const revoke = async (mail) => { try { await revokeOrgInvite(orgId, mail); flash('Revoked'); await load(); } catch (e) { flash('Failed', 'error'); } };
  const setPolicy = async (patch) => { try { await updateOrg(orgId, patch); flash('Updated'); await load(); } catch (e) { flash(e.response?.data?.detail || 'Failed', 'error'); } };
  const copyLink = (path, key) => { navigator.clipboard?.writeText(window.location.origin + path); setCopied(key); setTimeout(() => setCopied(null), 1500); };

  if (!orgId) return <div className="text-fg-muted">Select an organization first (see the switcher).</div>;
  if (loading) return <div className="text-fg-muted py-16 text-center">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-lg bg-orange/10"><Building2 className="w-5 h-5 text-orange" /></div>
        <div>
          <h1 className="text-2xl font-bold text-fg">{org?.name}</h1>
          <p className="text-fg-muted text-sm">{org?.member_count} member{org?.member_count !== 1 ? 's' : ''} · you are {org?.role}</p>
        </div>
      </div>

      {msg && <div className={`mb-4 p-3 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>{msg.text}</div>}

      {/* org visibility / join policy (admin) */}
      {isAdmin && (
        <div className="mb-6 bg-panel border border-panel-border rounded-xl p-5">
          <div className="text-sm font-semibold text-fg mb-3">Access</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {['private', 'public'].map((v) => (
              <button key={v} onClick={() => setPolicy({ visibility: v })}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${org.visibility === v ? 'border-orange bg-orange/10 text-fg' : 'border-panel-border text-fg-muted'}`}>
                {v === 'public' ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                {v === 'public' ? 'Public' : 'Private'}
              </button>
            ))}
          </div>
          {org.visibility === 'public' && (
            <div className="flex gap-2">
              {[['approval', 'Admin approves requests'], ['auto', 'Auto-approve (join instantly)']].map(([v, label]) => (
                <button key={v} onClick={() => setPolicy({ join_policy: v })}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${org.join_policy === v ? 'border-orange bg-orange/10 text-fg' : 'border-panel-border text-fg-muted'}`}>{label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* pending join requests (admin) */}
      {isAdmin && requests.length > 0 && (
        <div className="mb-6 bg-panel border border-panel-border rounded-xl p-5">
          <div className="text-sm font-semibold text-fg mb-3">Join requests ({requests.length})</div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.request_id} className="flex items-center justify-between text-sm">
                <span className="text-fg">{r.name || r.email}<span className="text-fg-muted text-xs ml-2">{r.email}</span></span>
                <div className="flex items-center gap-2">
                  <button onClick={() => decide(r.request_id, true)} className="flex items-center gap-1 px-2.5 py-1 bg-green-500/15 text-green-400 rounded text-xs hover:bg-green-500/25"><Check className="w-3.5 h-3.5" />Approve</button>
                  <button onClick={() => decide(r.request_id, false)} className="flex items-center gap-1 px-2.5 py-1 text-red-400 rounded text-xs hover:bg-red-500/10"><X className="w-3.5 h-3.5" />Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* invite (admin) */}
      {isAdmin && (
        <div className="mb-6 bg-panel border border-panel-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3"><UserPlus className="w-4 h-4 text-orange" /><span className="text-sm font-semibold text-fg">Invite by email</span></div>
          <div className="flex flex-wrap items-start gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com or search users"
                className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange" />
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-panel border border-panel-border rounded-lg shadow-lg max-h-56 overflow-auto">
                  {results.map((u) => (
                    <button key={u.email} disabled={u.is_member} onClick={() => { setEmail(u.email); setResults([]); }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-fg/5 disabled:opacity-40">
                      <span className="truncate text-fg">
                        {u.name || u.email}
                        {u.name && <span className="text-fg-muted text-xs ml-1.5">{u.email}</span>}
                      </span>
                      {/* Both outcomes need saying. A member can't be invited
                          twice (the row is disabled); a non-member match means
                          the address already has an account, so the invite
                          reaches them in-app as well as by email. */}
                      <span className="text-[10px] text-fg-muted shrink-0">
                        {u.is_member ? 'already a member' : 'has an account'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={invite} className="px-4 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm">Invite</button>
          </div>
          {invites.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {invites.map((i) => (
                <div key={i.email} className="flex items-center justify-between text-sm">
                  <span className="text-fg-muted">{i.email} · {i.role}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => copyLink(i.accept_path, i.email)} className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg">{copied === i.email ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />} link</button>
                    <button onClick={() => revoke(i.email)} className="text-xs text-red-400 hover:underline">revoke</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* members */}
      <div className="relative mb-3 max-w-sm">
        <Search className="w-4 h-4 text-fg-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={memberSearchInput} onChange={(e) => setMemberSearchInput(e.target.value)}
          placeholder="Search members by name or email…"
          className="w-full pl-9 pr-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange" />
      </div>
      <div className="bg-panel border border-panel-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-fg-muted border-b border-panel-border"><th className="py-3 px-5 font-medium">Member</th><th className="py-3 px-3 font-medium">Role</th><th className="py-3 px-3 w-12"></th></tr></thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={3} className="py-8 px-5 text-center text-fg-muted">No members found</td></tr>
            ) : members.map((m) => (
              <tr key={m.user_id} className="border-b border-panel-border/50">
                <td className="py-2.5 px-5"><div className="text-fg">{m.name || '—'}</div><div className="text-fg-muted text-xs">{m.email}</div></td>
                <td className="py-2.5 px-3">
                  {/* The server decides what's offered here — see rbac.allowed_member_actions. */}
                  {m.can_update_role ? (
                    <select value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)} className="px-2 py-1 bg-field border border-field-border rounded text-fg text-xs focus:border-orange">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : <span className="text-fg-muted">{m.role}</span>}
                </td>
                <td className="py-2.5 px-3 text-right whitespace-nowrap">
                  {m.can_transfer_ownership && (
                    <button onClick={() => transfer(m.user_id, m.email)} className="px-2 py-1 mr-1 text-xs text-fg-muted hover:text-orange">Make owner</button>
                  )}
                  {m.can_remove && (
                    <button onClick={() => remove(m.user_id)} className="p-1 text-fg-muted hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 rounded-lg bg-field border border-field-border text-fg disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-fg-muted text-sm">Page {page} of {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
            className="p-2 rounded-lg bg-field border border-field-border text-fg disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
